import type { ClaimResponse, SaveClaimRequest } from "../../../apis/claimService";
import { isOpVisitType } from "./newPatientDetailsConstants";

export const isOptionalClaimDocumentLabel = (label: string): boolean =>
  label.trim().toLowerCase() === "other";

export const IP_MANDATORY_CLAIM_DOCUMENT_LABELS = [
  "Hospital bill",
  "ICP",
  "Investigation Report",
] as const;

export const OP_MANDATORY_CLAIM_DOCUMENT_LABELS = [
  "Consultation note",
  "Hospital bill",
] as const;

export const isIpVisitType = (visitType?: string | null): boolean =>
  !isOpVisitType(visitType);

export const OP_CLAIM_AMOUNT_FIELDS_HOSPITAL = [
  "Total Claim Amount",
  "Hospital Bill Date",
  "Hospital Bill Number",
  "Hospital Bill Amount",
] as const;

export const OP_CLAIM_AMOUNT_FIELDS_AUTHORITY = [
  "Total Claim Amount",
  "Hospital Bill Date",
  "Hospital Bill Number",
  "Hospital Bill Amount",
] as const;

export const IP_CLAIM_AMOUNT_FIELD_ROWS = [
  [
    "Hospital Bill Amount",
    "Hospital Bill Date",
    "Hospital Bill Number",
    "Pharmacy Amount",
    "Consumable Amount",
    "Implant Amount (If Applicable)",
  ],
  ["Claim Amount", "Post Discharge Medicine Amount", "Unlisted Total"],
] as const;

export const OP_CLAIM_DOCUMENTS = [
  "Consultation note",
  "Hospital bill",
  "Investigation Report",
  "Other",
] as const;

export const OP_CLAIM_DOCUMENTS_AUTHORITY = [
  "consultation note",
  "Hospital bill",
  "investigation report",
  "Other",
] as const;

export const IP_CLAIM_DOCUMENTS = [
  "Hospital bill",
  "Pharmacy Bill",
  "ICP",
  "Investigation Report",
  "Implant Invoices",
  "Pouches",
  "Other",
] as const;

export const billFieldsFromClaim = (
  data: ClaimResponse,
  isIp: boolean,
): Record<string, string> => {
  if (isIp) {
    return {
      "Hospital Bill Amount": data.billAmount || "",
      "Hospital Bill Date": data.billDate || "",
      "Hospital Bill Number": data.billNumber || "",
      "Pharmacy Amount": data.drugAmount || "",
      "Consumable Amount": data.consumableAmount || "",
      "Implant Amount (If Applicable)": data.implantAmount || "",
      "Claim Amount": data.totalClaimAmount || "",
      "Post Discharge Medicine Amount": data.postDischargeMedicineAmount || "",
      "Unlisted Total": data.unlistedTotal || "",
    };
  }

  return {
    "Total Claim Amount": data.totalClaimAmount || "",
    "Hospital Bill Date": data.billDate || "",
    "Hospital Bill Number": data.billNumber || "",
    "Hospital Bill Amount": data.billAmount || "",
  };
};

export const savePayloadFromBillFields = (
  billFields: Record<string, string>,
  isIp: boolean,
): Pick<
  SaveClaimRequest,
  | "totalBillAmount"
  | "totalClaimAmount"
  | "billDate"
  | "billNumber"
  | "billAmount"
  | "drugAmount"
  | "consumableAmount"
  | "postDischargeMedicineAmount"
  | "implantAmount"
  | "unlistedTotal"
> => {
  if (isIp) {
    return {
      billDate: billFields["Hospital Bill Date"],
      billNumber: billFields["Hospital Bill Number"],
      billAmount: billFields["Hospital Bill Amount"],
      drugAmount: billFields["Pharmacy Amount"],
      consumableAmount: billFields["Consumable Amount"],
      implantAmount: billFields["Implant Amount (If Applicable)"],
      totalClaimAmount: billFields["Claim Amount"],
      postDischargeMedicineAmount: billFields["Post Discharge Medicine Amount"],
      unlistedTotal: billFields["Unlisted Total"],
    };
  }

  return {
    totalClaimAmount: billFields["Total Claim Amount"],
    billDate: billFields["Hospital Bill Date"],
    billNumber: billFields["Hospital Bill Number"],
    billAmount: billFields["Hospital Bill Amount"],
  };
};

export const isClaimAmountInputLabel = (label: string): boolean =>
  (/amount|total/i.test(label) && !/date|number/i.test(label)) ||
  label === "Unlisted Total";

export const parseClaimMoneyValue = (value?: string | null): number => {
  if (!value) return 0;
  const cleaned = String(value).replace(/[₹,\s]/g, "");
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

export const getClaimAmountFieldLabel = (isIp: boolean): string =>
  isIp ? "Claim Amount" : "Total Claim Amount";

export const isPrimaryClaimAmountLabel = (
  label: string,
  isIp: boolean,
): boolean => label === getClaimAmountFieldLabel(isIp);

export const getMaxClaimAmountFromBill = (
  hospitalBillAmount: string | undefined,
  beneficiaryCollected: boolean,
  beneficiaryAmount: string | undefined,
): number => {
  const bill = parseClaimMoneyValue(hospitalBillAmount);
  const beneficiary = beneficiaryCollected
    ? parseClaimMoneyValue(beneficiaryAmount)
    : 0;
  return Math.max(0, bill - beneficiary);
};

export const formatClaimMoneyHint = (value: number): string =>
  `₹ ${value.toLocaleString("en-IN")}/-`;

export const CLAIM_AMOUNT_EXCEEDS_HOSPITAL_BILL_MESSAGE =
  "Claim amount can not be greater than hospital bill amount.";

export const getProcedureTotalLessThanHospitalBillMessage = (
  hospitalAmount: number,
  procedureAmount: number,
): string =>
  `hospital amount is ${hospitalAmount} but procedure amount is ${procedureAmount}`;

/** Prefill claim Hospital Bill Amount from discharge when claim has none yet. */
export const withHospitalBillFromDischarge = (
  billFields: Record<string, string>,
  dischargeTotalBillAmount?: string | null,
): Record<string, string> => {
  if (parseClaimMoneyValue(billFields["Hospital Bill Amount"]) > 0) {
    return billFields;
  }
  const dischargeAmount = String(dischargeTotalBillAmount || "").trim();
  if (parseClaimMoneyValue(dischargeAmount) <= 0) {
    return billFields;
  }
  return {
    ...billFields,
    "Hospital Bill Amount": dischargeAmount,
  };
};

export const isClaimAmountOverHospitalBill = (
  billFields: Record<string, string>,
  isIp: boolean,
  beneficiaryCollected: boolean,
  beneficiaryAmount: string | undefined,
): boolean => {
  const hospitalBill = parseClaimMoneyValue(billFields["Hospital Bill Amount"]);
  if (hospitalBill <= 0) {
    return false;
  }
  const claimAmount = parseClaimMoneyValue(
    billFields[getClaimAmountFieldLabel(isIp)],
  );
  const maxClaim = getMaxClaimAmountFromBill(
    billFields["Hospital Bill Amount"],
    beneficiaryCollected,
    beneficiaryAmount,
  );
  return claimAmount > maxClaim;
};

export const validateClaimAmountAgainstBill = (
  billFields: Record<string, string>,
  isIp: boolean,
  beneficiaryCollected: boolean,
  beneficiaryAmount: string,
): string | null => {
  if (
    !isClaimAmountOverHospitalBill(
      billFields,
      isIp,
      beneficiaryCollected,
      beneficiaryAmount,
    )
  ) {
    return null;
  }

  return CLAIM_AMOUNT_EXCEEDS_HOSPITAL_BILL_MESSAGE;
};

export const clampClaimAmountInBillFields = (
  fields: Record<string, string>,
  isIp: boolean,
  beneficiaryCollected: boolean,
  beneficiaryAmount: string | undefined,
): Record<string, string> => {
  const claimLabel = getClaimAmountFieldLabel(isIp);
  const hospitalBill = parseClaimMoneyValue(fields["Hospital Bill Amount"]);
  if (hospitalBill <= 0) {
    return fields;
  }
  const max = getMaxClaimAmountFromBill(
    fields["Hospital Bill Amount"],
    beneficiaryCollected,
    beneficiaryAmount,
  );
  const current = parseClaimMoneyValue(fields[claimLabel]);
  if (current <= max) {
    return fields;
  }
  return {
    ...fields,
    [claimLabel]: max > 0 ? String(max) : "",
  };
};

export const isMandatoryClaimDocumentLabel = (
  label: string,
  isIpVisit = false,
): boolean => {
  if (isOptionalClaimDocumentLabel(label)) return false;
  const normalized = label.trim().toLowerCase();
  const mandatoryLabels = isIpVisit
    ? IP_MANDATORY_CLAIM_DOCUMENT_LABELS
    : OP_MANDATORY_CLAIM_DOCUMENT_LABELS;
  return mandatoryLabels.some(
    (mandatoryLabel) => mandatoryLabel.toLowerCase() === normalized,
  );
};
